import { storyblokDataAttributes } from "@commerce-frontend/storyblok-preview/component";
import type { TableFragmentFragment } from "@commerce-frontend/types";
import { graphql } from "@commerce-frontend/types";
import { cn } from "@commerce-frontend/ui/helpers/styles";
import { cva } from "class-variance-authority";
import type { HTMLAttributes } from "react";

graphql(/* GraphQL */ `
	fragment TableCellFragment on ContentTableCell {
		value
	}

	fragment TableFragment on Table {
		__typename
		id
		style
		dividers
		width
		table {
			thead {
				...TableCellFragment
			}
			tbody {
				body {
					...TableCellFragment
				}
			}
		}
	}
`);

export type TableBlockProps = {
	data: TableFragmentFragment;
} & HTMLAttributes<HTMLTableElement>;

export const Table = ({ data, className, ...props }: TableBlockProps) => {
	const { table, width, style, dividers } = data;

	const tbody = style === "highlightTop" ? table.tbody : [{ body: table.thead }, ...table.tbody];

	return (
		<div
			className={cn(
				"overflow-x-auto snap-x snap-mandatory md:overflow-x-hidden bg-white w-full shadow-1",
				className,
			)}
			{...storyblokDataAttributes(data)}
		>
			<table
				{...props}
				className={cn(
					"border-t border-r border-b border-gray-200 min-w-full md:w-full w-fit",
					width === "fit" ? "table-fixed" : "md:table-fixed",
				)}
			>
				{style === "highlightTop" ? (
					<thead className="bg-gray-50 border-b border-b-gray-200">
						<tr>
							{table.thead.map((cell, key) => (
								<th
									key={`header_col_${key}`}
									className={cellStyles({
										width,
									})}
								>
									{cell.value}
								</th>
							))}
						</tr>
					</thead>
				) : null}
				<tbody>
					{tbody.map((row, rowKey) => (
						<tr
							key={`row_${rowKey}`}
							className={cn(
								style === "zebra" && "even:bg-gray-50",
								(dividers === "horizontal" || dividers === "all") &&
									"last:border-b-0 border-b border-gray-200",
							)}
						>
							{row.body.map((cell, colKey) => (
								<td
									key={`row_${rowKey}_col=${colKey}`}
									className={cellStyles({
										width,
										highlightLeft: style === "highlightLeft",
										verticalDividers: dividers === "vertical" || dividers === "all",
									})}
								>
									{cell.value}
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};

export const cellStyles = cva(
	"first:border-l border-gray-200 px-3xl py-xl text-left text-sm font-normal text-gray-600 md:min-w-min snap-start text-wrap break-words align-top",
	{
		variants: {
			width: {
				fit: "w-full max-w-[calc(100vw-3rem)]",
				scroll: "min-w-[calc(100vw-3rem)]",
			},
			highlightLeft: {
				true: "first:bg-gray-50 first:border-r first:font-medium",
				false: "",
			},
			verticalDividers: {
				true: "last:border-r-0 border-r",
				false: "",
			},
		},
	},
);
